import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { UIAvatar, UICard, UIDateRangeFields } from "../../../../../ui";
import EventPayrollAdjustmentSection from "./EventPayrollAdjustmentSection";
import EventPayrollEarningsSection from "./EventPayrollEarningsSection";
import EventPayrollSummaryMetrics from "./EventPayrollSummaryMetrics";
import { Box } from "@mui/material";
import { CalendarX, GraduationCap } from "lucide-react";

import "../../../../../Card/_Card.scss";

const EventPayrollApplicantPayrollView = ({
  data,
  labels,
  showStcNumber,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onAddDeduction,
  onAddReward,
  onRevertDeductionEntry,
  onRevertRewardEntry,
  canRevert,
}) => {
  const sectionLabels = labels.sections;
  const summaryItems = useMemo(
    () => [
      {
        key: "baseEarnings",
        label: labels.summary.baseEarnings,
        value: data.summary.baseEarnings,
      },
      {
        key: "deductions",
        label: labels.summary.deductions,
        value: data.summary.deductions,
        signed: true,
      },
      {
        key: "rewards",
        label: labels.summary.rewards,
        value: data.summary.rewards,
        signed: true,
      },
      {
        key: "training",
        label: labels.summary.training,
        value: data.summary.training,
        signed: true,
      },
      {
        key: "netPay",
        label: labels.summary.finalNetPay,
        value: data.summary.netPay,
        isCurrency: true,
      },
    ],
    [data.summary, labels.summary],
  );

  const baseEarningsMeta = sectionLabels.baseEarningsMeta
    .replace("{roles}", String(data.baseEarnings.roleCount))
    .replace("{days}", String(data.baseEarnings.totalDays));

  const trainingMeta = sectionLabels.trainingMeta
    .replace("{roles}", String(data.training.roleCount))
    .replace("{days}", String(data.training.totalDays));

  const deductionsMeta = sectionLabels.deductionsMeta.replace(
    "{count}",
    String(data.deductions.entryCount),
  );

  const rewardsMeta = sectionLabels.rewardsMeta.replace(
    "{count}",
    String(data.rewards.entryCount),
  );

  return (
    <div className="event-payroll-generate__single">
      <header className="event-payroll-generate__page-header">
        <h2 className="event-payroll-generate__page-header-title text-heading-xl">
          {labels.singleTitle}
        </h2>
        <p className="event-payroll-generate__page-header-subtitle text-body-sm">
          {labels.singleSubtitle}
        </p>
      </header>

      <UICard
        type="info"
        className="event-payroll-generate__header-card ui-card--section"
        title={labels.applicantDetails}
      >
        <div className="event-payroll-generate__header-card-inner">
          <Box display="flex" gap={2} alignItems="center">
            <UIAvatar
              name={data.displayName}
              image={data.applicant.image}
              size="lg"
            />
            <div className="event-detail-field event-payroll-generate__applicant-detail">
              <span className="event-detail-label">{labels.staffName}</span>
              <span className="event-detail-value">
                {data.displayName || "—"}
              </span>
            </div>

            {showStcNumber && (
              <div className="event-detail-field event-payroll-generate__applicant-detail">
                <span className="event-detail-label">{labels.stcNo}</span>
                <span className="event-detail-value">
                  {data.applicant.stcNumber || "—"}
                </span>
              </div>
            )}
          </Box>

          <div className="event-payroll-generate__period">
            <span className="event-detail-label">{labels.reportPeriod}</span>
            <UIDateRangeFields
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
              startPlaceholder={labels.startDate}
              endPlaceholder={labels.endDate}
              rangeErrorMessage={labels.endDateBeforeStartDate}
              classNames={{
                start: "event-payroll-generate__period-date",
                end: "event-payroll-generate__period-date",
              }}
            />
          </div>
        </div>
      </UICard>

      <EventPayrollSummaryMetrics
        items={summaryItems}
        labels={labels.summary}
      />

      <div className="event-payroll-generate__details-grid">
        <EventPayrollEarningsSection
          title={sectionLabels.baseEarnings}
          meta={baseEarningsMeta}
          total={data.baseEarnings.total}
          roles={data.baseEarnings.roles}
          labels={sectionLabels}
          shiftLabels={labels.shifts}
          emptyIcon={<CalendarX size={24} />}
          emptyTitle={sectionLabels.baseEarningsEmptyTitle}
          emptyDescription={sectionLabels.baseEarningsEmptyDescription}
        />
        <EventPayrollAdjustmentSection
          variant="deduction"
          title={sectionLabels.deductions}
          meta={deductionsMeta}
          total={data.deductions.total}
          entries={data.deductions.entries}
          labels={sectionLabels}
          onAdd={onAddDeduction}
          onRevertEntry={onRevertDeductionEntry}
          canRevert={canRevert}
          emptyDescription={sectionLabels.deductionsEmptyDescription}
          emptyActionLabel={sectionLabels.addDeduction}
          onEmptyAction={onAddDeduction}
        />
        <EventPayrollEarningsSection
          title={sectionLabels.training}
          meta={trainingMeta}
          total={data.training.total}
          roles={data.training.roles}
          labels={sectionLabels}
          shiftLabels={labels.shifts}
          emptyIcon={<GraduationCap size={24} />}
          emptyTitle={sectionLabels.trainingEmptyTitle}
          emptyDescription={sectionLabels.trainingEmptyDescription}
        />
        <EventPayrollAdjustmentSection
          variant="reward"
          title={sectionLabels.rewards}
          meta={rewardsMeta}
          total={data.rewards.total}
          entries={data.rewards.entries}
          labels={sectionLabels}
          onAdd={onAddReward}
          onRevertEntry={onRevertRewardEntry}
          canRevert={canRevert}
          emptyDescription={sectionLabels.rewardsEmptyDescription}
          emptyActionLabel={sectionLabels.addReward}
          onEmptyAction={onAddReward}
        />
      </div>
    </div>
  );
};

EventPayrollApplicantPayrollView.propTypes = {
  data: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  showStcNumber: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onAddDeduction: PropTypes.func,
  onAddReward: PropTypes.func,
  onRevertDeductionEntry: PropTypes.func,
  onRevertRewardEntry: PropTypes.func,
  canRevert: PropTypes.bool,
};

EventPayrollApplicantPayrollView.defaultProps = {
  showStcNumber: false,
  startDate: "",
  endDate: "",
  onStartDateChange: undefined,
  onEndDateChange: undefined,
  onAddDeduction: undefined,
  onAddReward: undefined,
  onRevertDeductionEntry: undefined,
  onRevertRewardEntry: undefined,
  canRevert: true,
};

export default EventPayrollApplicantPayrollView;
